import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import {
  type AssetFilters,
  FIXED_ASSET_STATUSES,
  type FixedAssetStatus,
} from '@/features/accounting-assets/api'
import { AssetList } from '@/features/accounting-assets/asset-list'
import { hasGrant } from '@/lib/me'

function isAssetStatus(value: unknown): value is FixedAssetStatus {
  return typeof value === 'string' && (FIXED_ASSET_STATUSES as readonly string[]).includes(value)
}

/**
 * Route shell for the fixed-asset register.
 *
 * The register's search and status filter live in the URL search params so a
 * filtered view is linkable and survives a reload; `AssetList` matches them
 * client-side, because the endpoint takes no query parameters.
 *
 * ## Why this renders an Outlet
 *
 * `assets/depreciation.tsx` is a CHILD of this route in flat file routing, so
 * without an Outlet it never mounts and `/accounting/assets/depreciation`
 * silently rendered this register instead. Same shape and same fix as
 * `hr/leave.tsx` and `hr/expenses.tsx`.
 */
export const Route = createFileRoute('/_authenticated/accounting/assets')({
  validateSearch: (search: Record<string, unknown>): AssetFilters => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: isAssetStatus(search.status) ? search.status : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_asset.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingAssetsRoute,
})

function AccountingAssetsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <AssetList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/assets', search: next })}
    />
  )
}
